import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { Forbidden } from '../components/Forbidden';
import { toastApiError } from '../lib/apiToast';

interface AdminUserRow {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  createdAt: string;
  updatedAt: string;
  keycloak_id: string | null;
  phone_e164: string | null;
  preferred_locale: string | null;
  signup_method: string | null;
  email_verified_at: string | null;
  /** NULL = still subscribed to non-transactional email; set = opted out. */
  marketing_emails_opt_out_at: string | null;
}

interface UsersPage {
  items: AdminUserRow[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

type ViewState =
  | { kind: 'loading' }
  | { kind: 'ready'; page: UsersPage }
  | { kind: 'error' }
  | { kind: 'forbidden' };

const PAGE_SIZE = 20;

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

export const UsersPage: React.FC = () => {
  const { token, hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const [state, setState] = useState<ViewState>({ kind: 'loading' });
  const [page, setPage] = useState(1);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!isAdmin) {
      setState({ kind: 'forbidden' });
      return;
    }
    setState({ kind: 'loading' });
    api.setToken(token);
    const res = await api.get<UsersPage>(
      `/users?page=${page}&pageSize=${PAGE_SIZE}&sortBy=createdAt&sortOrder=desc`,
    );
    if (res.success && res.data) {
      setState({ kind: 'ready', page: res.data });
      return;
    }
    if (res.error?.code === 'FORBIDDEN') {
      setState({ kind: 'forbidden' });
      return;
    }
    toastApiError(res, 'Failed to load users');
    setState({ kind: 'error' });
  }, [isAdmin, token, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const deleteUser = useCallback(
    async (user: AdminUserRow) => {
      const ok = window.confirm(
        `Delete ${user.email}?\n\nThis removes the user from Keycloak and the database (and all linked documents/chats). This cannot be undone.`,
      );
      if (!ok) return;
      setDeletingId(user.id);
      api.setToken(token);
      const res = await api.delete<void>(`/users/${user.id}`);
      setDeletingId(null);
      if (!res.success) {
        toastApiError(res, 'Failed to delete user');
        return;
      }
      void load();
    },
    [token, load],
  );

  if (state.kind === 'forbidden') return <Forbidden />;

  const rows = state.kind === 'ready' ? state.page.items : [];
  const totalPages = state.kind === 'ready' ? Math.max(1, state.page.totalPages) : 1;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-medium text-gray-900">User Management</h2>
        <div className="text-sm text-gray-500">
          {state.kind === 'ready' ? `${state.page.total} total` : ''}
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Phone
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Identity
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email subscribed
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {state.kind === 'loading' ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            ) : state.kind === 'error' ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-red-600">
                  Failed to load users.{' '}
                  <button onClick={() => void load()} className="underline">
                    Retry
                  </button>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                  No users found
                </td>
              </tr>
            ) : (
              rows.map((user) => {
                const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
                return (
                  <tr key={user.id}>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {fullName || <span className="text-gray-400">—</span>}
                      </div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {user.phone_e164 || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs">
                      <div className="flex flex-col gap-1">
                        <span
                          className={`inline-flex w-fit px-2 py-0.5 rounded-full font-medium ${
                            user.keycloak_id
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {user.keycloak_id ? 'in Keycloak' : 'local only'}
                        </span>
                        {user.email_verified_at ? (
                          <span className="text-gray-500">verified</span>
                        ) : (
                          <span className="text-gray-400">unverified</span>
                        )}
                        {user.signup_method && (
                          <span className="text-gray-400">via {user.signup_method}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs">
                      {user.marketing_emails_opt_out_at ? (
                        <span
                          className="inline-flex w-fit px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-600"
                          title={`Unsubscribed ${formatDate(user.marketing_emails_opt_out_at)}. Covers reminder/job emails; transactional mail still goes out.`}
                        >
                          unsubscribed
                        </span>
                      ) : (
                        <span className="inline-flex w-fit px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-800">
                          subscribed
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                      <Link
                        to={`/users/${user.id}`}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        History
                      </Link>
                      <button
                        onClick={() => void deleteUser(user)}
                        disabled={deletingId === user.id}
                        className="text-red-600 hover:text-red-900 disabled:opacity-50"
                      >
                        {deletingId === user.id ? 'Deleting…' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>

      {state.kind === 'ready' && totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1 rounded border disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-gray-500">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1 rounded border disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};
